import { ReactNode, useCallback, useId } from 'react';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import { SelectPaginate, SelectPaginateOption } from 'ui';

export interface ClientSelectOption extends SelectPaginateOption<number> {
  email?: string | null;
  correspondence_email?: string | null;
}

type baseProps = {
  label?: string;
  placeholder?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  isClearable?: boolean;
};

type singleProps = {
  isMulti: false;
  value?: ClientSelectOption;
  onChange: (option: ClientSelectOption | null, title?: string) => void;
  defaultValue?: ClientSelectOption;
};

type multiProps = {
  isMulti: true;
  value?: ClientSelectOption[];
  onChange: (options: ClientSelectOption[], title?: string) => void;
  defaultValue?: ClientSelectOption[];
};

type ClientsSelectPaginatedProps = baseProps & (singleProps | multiProps);

const ClientsSelectPaginated = ({
  label = 'Client (optional)',
  error,
  value,
  isMulti,
  className,
  placeholder,
  defaultValue,
  formFieldTitle,
  onChange,
  disabled = false,
  isClearable = false,
}: ClientsSelectPaginatedProps) => {
  const { fetchNextPage } = useClientsListQuery(
    {
      page: 1,
      page_size: 15,
      filter_by_name: '',
    },
    true,
    { cacheKey: useId() },
  );

  const handleFetchNextClients = async (
    page: number,
    search?: string,
  ): Promise<ClientSelectOption[]> => {
    const clientsData = await fetchNextPage({ pageParam: { page, search } });

    const clients = clientsData?.data?.pages?.at(-1)?.data || [];

    return clients.map(({ id, name, email, correspondence_email }) => ({
      label: name as ReactNode,
      value: id,
      email,
      correspondence_email,
    }));
  };

  const handleChange = useCallback(
    (options: ClientSelectOption | ClientSelectOption[] | null) => {
      if (!isMulti && options === null) {
        onChange(null as any, formFieldTitle);
        return;
      }

      if (Array.isArray(options) && options.length === 0) {
        onChange(options as any, formFieldTitle);
        return;
      }

      if (!Array.isArray(options) && (!options || !options.value)) return;
      if (Array.isArray(options) && options.some(({ value }) => !value)) return;

      onChange(options as any, formFieldTitle);
    },
    [formFieldTitle, isMulti, onChange],
  );

  return (
    <SelectPaginate
      autoHeight
      placeholder={placeholder}
      isMulti={isMulti}
      disabled={disabled}
      defaultValue={defaultValue}
      error={error}
      value={value || null}
      className={className}
      onChange={handleChange}
      label={label}
      onAsync={handleFetchNextClients}
      debounceTimeoutSearch={700}
      isClearable={isClearable}
    />
  );
};

export default ClientsSelectPaginated;
